import React, { useEffect, useState } from 'react';
import useLockBodyScroll from '../../hooks/useLockBodyScroll';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { resolveImageUrl, getOptimizedImageUrl } from '../../utils/image';
import useBackButton from '../../hooks/useBackButton';

interface ImageViewerModalProps {
  images: string[];
  thumbnailImages?: string[];
  mediumImages?: string[];
  initialIndex?: number;
  onClose: () => void;
}

const ImageViewerModal: React.FC<ImageViewerModalProps> = ({ images, thumbnailImages, mediumImages, initialIndex = 0, onClose }) => {
  useLockBodyScroll();
  useBackButton(onClose, true, 100);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  const handleNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!images.length) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 z-[200] flex flex-col items-center justify-center p-4"
        onClick={onClose}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors z-50 shadow-md"
          style={{ top: 'calc(max(var(--safe-area-inset-top, 0px), env(safe-area-inset-top, 0px)) + 1rem)' }}
        >
          <XMarkIcon className="w-8 h-8" />
        </button>

        {/* Main Image Area */}
        <div className="flex-1 w-full flex items-center justify-center relative max-h-[80vh]">
          {/* Prev Button */}
          {images.length > 1 && (
            <button
              onClick={handlePrev}
              className="absolute left-4 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors z-40"
            >
              <ChevronLeftIcon className="w-8 h-8" />
            </button>
          )}

          <motion.img
            key={currentIndex}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            src={mediumImages?.[currentIndex] || getOptimizedImageUrl(images[currentIndex], 'medium')}
            alt={`View ${currentIndex + 1}`}
            className="max-w-full max-h-[80vh] max-w-[90vw] object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next Button */}
          {images.length > 1 && (
            <button
              onClick={handleNext}
              className="absolute right-4 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors z-40"
            >
              <ChevronRightIcon className="w-8 h-8" />
            </button>
          )}
        </div>

        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="h-24 w-full max-w-4xl mt-4 flex items-center justify-start md:justify-center gap-2 overflow-x-auto px-4 scrollbar-hide" onClick={(e) => e.stopPropagation()}>
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                  currentIndex === idx ? 'border-teal-500 scale-110' : 'border-transparent opacity-50 hover:opacity-100'
                }`}
              >
                <img src={thumbnailImages?.[idx] || getOptimizedImageUrl(img, 'thumbnail')} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default ImageViewerModal;
